import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const PdfImportModal = ({ isOpen, onClose, onImport }) => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [extractedData, setExtractedData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Editable extracted data
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedIngredients, setEditedIngredients] = useState([]);
  const [editedSteps, setEditedSteps] = useState([]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        setError('Please select a PDF file');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File must be smaller than 10MB');
        return;
      }
      setFile(selectedFile);
      setError('');
      setExtractedData(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('pdf', file);
      
      const response = await fetch('/api/import/pdf', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to extract recipe');
      }
      
      setExtractedData(data.data);
      setEditedName(data.data.name);
      setEditedDescription(data.data.description);
      setEditedIngredients(data.data.ingredients);
      setEditedSteps(data.data.steps.map(s => s.instruction));
      setEditMode(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleIngredientChange = (index, field, value) => {
    const updated = [...editedIngredients];
    updated[index][field] = value;
    setEditedIngredients(updated);
  };

  const handleAddIngredient = () => {
    setEditedIngredients([...editedIngredients, { name: '', quantity: '' }]);
  };

  const handleRemoveIngredient = (index) => {
    setEditedIngredients(editedIngredients.filter((_, i) => i !== index));
  };

  const handleStepChange = (index, value) => {
    const updated = [...editedSteps];
    updated[index] = value;
    setEditedSteps(updated);
  };

  const handleAddStep = () => {
    setEditedSteps([...editedSteps, '']);
  };

  const handleRemoveStep = (index) => {
    setEditedSteps(editedSteps.filter((_, i) => i !== index));
  };

  const handleImport = () => {
    const recipeData = {
      name: editedName,
      description: editedDescription,
      ingredients: editedIngredients.filter(i => i.name.trim()),
      steps: editedSteps.filter(s => s.trim())
    };
    onImport(recipeData);
    handleClose();
  };

  const handleClose = () => {
    setFile(null);
    setExtractedData(null);
    setEditMode(false);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>📄 Import Recipe from PDF</h2>
          <button onClick={handleClose} style={styles.closeBtn}>×</button>
        </div>
        
        {error && <div style={styles.error}>{error}</div>}
        
        {!editMode ? (
          <div style={styles.uploadSection}>
            <div 
              style={styles.dropZone}
              onClick={() => fileInputRef.current?.click()}
            >
              <div style={styles.dropIcon}>📄</div>
              <p style={styles.dropText}>
                {file ? file.name : 'Click to select a PDF file'}
              </p>
              <p style={styles.dropHint}>
                Supported: Recipe PDFs with ingredients and instructions
              </p>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileSelect}
              style={styles.hiddenInput}
            />
            
            {file && (
              <button 
                onClick={handleUpload} 
                style={styles.uploadBtn}
                disabled={loading}
              >
                {loading ? '🔄 Extracting...' : '📤 Extract Recipe'}
              </button>
            )}
            
            <div style={styles.helpText}>
              <p><strong>Tips for best results:</strong></p>
              <ul style={styles.helpList}>
                <li>PDF should have clear "Ingredients" and "Instructions" sections</li>
                <li>Works best with structured recipe formats</li>
                <li>You can edit the extracted data before importing</li>
              </ul>
            </div>
          </div>
        ) : (
          <div style={styles.editSection}>
            <div style={styles.successBanner}>
              ✅ Extracted {editedIngredients.length} ingredients and {editedSteps.length} steps
            </div>
            
            <div style={styles.field}>
              <label style={styles.label}>Recipe Name</label>
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                style={styles.input}
                placeholder="Recipe name"
              />
            </div>
            
            <div style={styles.field}>
              <label style={styles.label}>Description</label>
              <textarea
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                style={styles.textarea}
                placeholder="Brief description"
                rows={2}
              />
            </div>
            
            <div style={styles.field}>
              <div style={styles.sectionHeader}>
                <label style={styles.label}>🥕 Ingredients</label>
                <button type="button" onClick={handleAddIngredient} style={styles.addBtn}>+ Add</button>
              </div>
              <div style={styles.scrollList}>
                {editedIngredients.map((ing, index) => (
                  <div key={index} style={styles.ingredientRow}>
                    <input
                      type="text"
                      value={ing.quantity}
                      onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                      style={styles.qtyInput}
                      placeholder="Qty"
                    />
                    <input
                      type="text"
                      value={ing.name}
                      onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                      style={styles.nameInput}
                      placeholder="Ingredient"
                    />
                    <button 
                      onClick={() => handleRemoveIngredient(index)} 
                      style={styles.removeBtn}
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
            
            <div style={styles.field}>
              <div style={styles.sectionHeader}>
                <label style={styles.label}>📝 Steps</label>
                <button type="button" onClick={handleAddStep} style={styles.addBtn}>+ Add</button>
              </div>
              <div style={styles.scrollList}>
                {editedSteps.map((step, index) => (
                  <div key={index} style={styles.stepRow}>
                    <span style={styles.stepNum}>{index + 1}.</span>
                    <textarea
                      value={step}
                      onChange={(e) => handleStepChange(index, e.target.value)}
                      style={styles.stepInput}
                      placeholder="Step instruction"
                      rows={2}
                    />
                    <button 
                      onClick={() => handleRemoveStep(index)} 
                      style={styles.removeBtn}
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
            
            <div style={styles.actions}>
              <button onClick={() => setEditMode(false)} style={styles.backBtn}>
                ← Back
              </button>
              <button onClick={handleImport} style={styles.importBtn}>
                ✓ Import Recipe
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem'
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e5e7eb'
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    color: '#1e3a5f'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#6b7280'
  },
  error: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '0.75rem 1.5rem',
    margin: '0'
  },
  uploadSection: {
    padding: '1.5rem',
    overflowY: 'auto'
  },
  dropZone: {
    border: '2px dashed #d1d5db',
    borderRadius: '8px',
    padding: '2rem',
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: '#f9fafb',
    transition: 'border-color 0.2s'
  },
  dropIcon: {
    fontSize: '3rem',
    marginBottom: '0.5rem'
  },
  dropText: {
    color: '#374151',
    fontWeight: '500',
    margin: '0 0 0.25rem 0'
  },
  dropHint: {
    color: '#9ca3af',
    fontSize: '0.85rem',
    margin: '0'
  },
  hiddenInput: {
    display: 'none'
  },
  uploadBtn: {
    width: '100%',
    padding: '0.75rem',
    marginTop: '1rem',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer'
  },
  helpText: {
    marginTop: '1.5rem',
    padding: '1rem',
    backgroundColor: '#f0f9ff',
    borderRadius: '8px',
    fontSize: '0.875rem',
    color: '#1e40af'
  },
  helpList: {
    margin: '0.5rem 0 0 1.5rem',
    padding: 0
  },
  editSection: {
    padding: '1rem 1.5rem',
    overflowY: 'auto',
    flex: 1
  },
  successBanner: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    padding: '0.75rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    textAlign: 'center',
    fontWeight: '500'
  },
  field: {
    marginBottom: '1rem'
  },
  label: {
    display: 'block',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '0.5rem'
  },
  input: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '0.9rem',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '0.9rem',
    resize: 'vertical',
    boxSizing: 'border-box'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem'
  },
  addBtn: {
    backgroundColor: '#22c55e',
    color: 'white',
    border: 'none',
    padding: '0.25rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer'
  },
  scrollList: {
    maxHeight: '150px',
    overflowY: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '0.5rem'
  },
  ingredientRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    alignItems: 'center'
  },
  qtyInput: {
    width: '70px',
    padding: '0.4rem',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    fontSize: '0.85rem'
  },
  nameInput: {
    flex: 1,
    padding: '0.4rem',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    fontSize: '0.85rem'
  },
  stepRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    alignItems: 'flex-start'
  },
  stepNum: {
    fontWeight: '600',
    color: '#f97316',
    minWidth: '20px',
    paddingTop: '0.4rem'
  },
  stepInput: {
    flex: 1,
    padding: '0.4rem',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    fontSize: '0.85rem',
    resize: 'vertical'
  },
  removeBtn: {
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    flexShrink: 0
  },
  actions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'space-between',
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid #e5e7eb'
  },
  backBtn: {
    backgroundColor: '#e5e7eb',
    color: '#374151',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600'
  },
  importBtn: {
    backgroundColor: '#22c55e',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600'
  }
};

export default PdfImportModal;
